import { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Copy, QrCode, Pencil, Send } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth, hasRole } from "@/context/AuthContext";
import { StatusBadge } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const tabs = [
  { to: "", label: "Overview", end: true },
  { to: "sessions", label: "Sessions" },
  { to: "attendees", label: "Attendees" },
  { to: "questions", label: "Questions" },
  { to: "polls", label: "Polls" },
  { to: "feedback", label: "Feedback" },
  { to: "surveys", label: "Surveys" },
  { to: "reports", label: "Reports" },
  { to: "settings", label: "Settings" },
];

export default function EventLayout() {
  const { id } = useParams();
  const { role } = useAuth();
  const [event, setEvent] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);

  const refresh = useCallback(() => {
    api.get(`/events/${id}`).then(({ data }) => setEvent(data)).catch(() => setEvent(false));
  }, [id]);
  useEffect(() => { refresh(); }, [refresh]);

  const attendeeUrl = `${window.location.origin}/attendee/${id}`;
  const copyLink = () => { navigator.clipboard.writeText(attendeeUrl); toast.success("Attendee link copied"); };

  const publish = async () => {
    try {
      await api.put(`/events/${id}`, { status: "published" });
      toast.success("Event published");
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (event === null) return <div className="p-5 sm:p-8 max-w-6xl"><Skeleton className="h-24 rounded-xl mb-4" /><Skeleton className="h-64 rounded-xl" /></div>;
  if (event === false) return <div className="p-8"><p className="text-muted-foreground">Event not found.</p><Link to="/events" className="text-accent">← Back to events</Link></div>;

  return (
    <div className="p-5 sm:p-8 max-w-6xl">
      <Link to="/events" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to events
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          {event.logo && <img src={event.logo} alt="" className="h-12 w-12 rounded-lg object-cover border border-border" />}
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-foreground">{event.name}</h1>
              <StatusBadge status={event.status} />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{event.venue || event.location || "No location"} · Code #{event.public_code}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {event.status === "draft" && hasRole(role, "moderator") && (
            <Button onClick={publish} className="rounded-md bg-accent text-accent-foreground hover:opacity-90 transition-opacity" data-testid="publish-event-btn">
              <Send className="h-3.5 w-3.5 mr-1.5" /> Publish
            </Button>
          )}
          {hasRole(role, "moderator") && (
            <Button asChild variant="outline" className="rounded-md"><Link to={`/events/${id}/edit`} data-testid="edit-event-link"><Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit</Link></Button>
          )}
          <Button variant="outline" className="rounded-md" onClick={() => setQrOpen(true)} data-testid="show-qr-btn"><QrCode className="h-3.5 w-3.5 mr-1.5" /> QR</Button>
          <button onClick={copyLink} data-testid="copy-attendee-link" className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-border hover:bg-muted transition-colors"><Copy className="h-3.5 w-3.5" /> Link</button>
          <a href={attendeeUrl} target="_blank" rel="noreferrer" data-testid="open-attendee-view" className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition-opacity"><ExternalLink className="h-3.5 w-3.5" /> Attendee view</a>
        </div>
      </div>

      <div className="mt-6 border-b border-border overflow-x-auto no-scrollbar">
        <nav className="flex gap-1 min-w-max">
          {tabs.map((t) => (
            <NavLink key={t.label} to={t.to} end={t.end} data-testid={`tab-${t.label.toLowerCase()}`}
              className={({ isActive }) => `px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${isActive ? "border-accent text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {t.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="mt-6"><Outlet context={{ event, refresh, attendeeUrl, role }} /></div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Attendee QR code</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2" data-testid="qr-dialog">
            <div className="p-4 bg-white rounded-lg border border-border"><QRCodeCanvas value={attendeeUrl} size={200} level="M" /></div>
            <p className="text-xs text-muted-foreground text-center break-all">{attendeeUrl}</p>
            <Button variant="outline" className="rounded-md w-full" onClick={copyLink}>Copy link</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
